import { useState, useEffect, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { courseService } from "../../services/courseService";
import { debounce } from "../../utils/debounce";

interface Course {
  _id: string;
  title: string;
  level?: string;
  price?: number;
}

interface CourseSelectorProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function CourseSelector({
  selectedIds,
  onChange,
  placeholder = "Search courses...",
  disabled = false,
}: CourseSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCoursesMap, setSelectedCoursesMap] = useState<Record<string, Course>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const fetchCourses = async (term: string) => {
    setIsLoading(true);
    try {
      const response = await courseService.getAllCourses({
        search: term,
        limit: 50,
      });
      const coursesList = response.data?.courses || response.courses || [];
      setCourses(coursesList);
    } catch (err) {
      console.error("Failed to fetch courses:", err);
      setCourses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetch = useRef(
    debounce((term: string) => {
      fetchCourses(term);
    }, 300)
  );

  useEffect(() => {
    if (isOpen) {
      debouncedFetch.current(searchTerm);
      searchInputRef.current?.focus();
    }
  }, [isOpen, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const handleSelectCourse = (courseId: string) => {
    if (!selectedIds.includes(courseId)) {
      onChange([...selectedIds, courseId]);
      const course = courses.find((c) => c._id === courseId);
      if (course) {
        setSelectedCoursesMap((prev) => ({ ...prev, [courseId]: course }));
      }
    }
    setSearchTerm("");
  };

  const handleRemoveCourse = (courseId: string) => {
    onChange(selectedIds.filter((id) => id !== courseId));
    setSelectedCoursesMap((prev) => {
      const next = { ...prev };
      delete next[courseId];
      return next;
    });
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      <label className="text-sm font-medium text-gray-700">
        Select Courses
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer flex items-center justify-between text-sm bg-white hover:border-gray-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="text-gray-600">
            {selectedIds.length > 0
              ? `${selectedIds.length} course${selectedIds.length > 1 ? "s" : ""} selected`
              : placeholder}
          </span>
          <div className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
            <Search size={16} className="text-gray-400" />
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
            <div className="p-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
              <Search size={14} className="text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                className="w-full bg-transparent border-none outline-none text-sm placeholder:text-gray-400"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              {isLoading && <Loader2 size={14} className="animate-spin text-gray-400" />}
            </div>

            <div className="max-h-60 overflow-y-auto">
              {isLoading && courses.length === 0 ? (
                <div className="px-4 py-8 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Loading courses...
                </div>
              ) : courses.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  No courses found
                </div>
              ) : (
                courses.map((course) => {
                  const isSelected = selectedIds.includes(course._id);
                  return (
                    <div
                      key={course._id}
                      className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-indigo-50 text-indigo-700 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => handleSelectCourse(course._id)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="truncate">{course.title}</span>
                        {isSelected && (
                          <X size={14} className="text-indigo-500 ml-2" />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {Object.keys(selectedCoursesMap).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {Object.values(selectedCoursesMap).map((course) => (
            <div
              key={course._id}
              className="flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-xs"
            >
              <span>{course.title}</span>
              <button
                type="button"
                onClick={() => handleRemoveCourse(course._id)}
                className="hover:text-indigo-900"
                aria-label={`Remove ${course.title}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
